

import { createContext, useContext, useState, useMemo, useCallback } from "react";
import { isValidObjectId } from "@/lib/utils/input-validator";
import { getGuestAppointmentsAllOfferings } from "@/lib/slotting/api";
import type { Appointment } from "@/lib/slotting/types";

type GuestAppointmentsMap = Map<string, Appointment[] | null>;

type GuestAppointmentsContextType = {
  loading: boolean;
  errorMessage: string | null;
  fetchAppointments: (guestId: string, refetch?: boolean) => Promise<void>;
  deleteAppointment: (appointmentId: string) => Promise<void>;
  allAppointmentsState: GuestAppointmentsMap;
};

const GuestAppointmentsContext = createContext<
  GuestAppointmentsContextType | undefined
>(undefined);

export const GuestAppointmentsProvider = ({
  children,
  eventId,
}: {
  children: React.ReactNode;
  eventId: string;
}) => {
  const [allAppointmentsState, setAllAppointmentsState] =
    useState<GuestAppointmentsMap>(new Map());
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchAppointments = useCallback(async (guestId: string, refetch?: boolean) => {
    setErrorMessage(null);

    if (loading) return;

    if (allAppointmentsState.has(guestId) && !refetch) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      if (!isValidObjectId(eventId)) throw new Error("validationError");
      if (!isValidObjectId(guestId)) throw new Error("validationError");

      const result = await getGuestAppointmentsAllOfferings(eventId, guestId);
      const appointments = result?.appointments ?? [];

      setAllAppointmentsState((prev) => {
        const newMap = new Map(prev);
        newMap.set(guestId, appointments);
        return newMap;
      });
    } catch (error) {
      let msg = "unknown";
      if (error instanceof Error) msg = error.message;
      setErrorMessage(msg);
      setAllAppointmentsState((prev) => {
        const newMap = new Map(prev);
        newMap.set(guestId, []);
        return newMap;
      });
    } finally {
      setLoading(false);
    }
  }, [eventId, allAppointmentsState, loading]);

  const deleteAppointment = useCallback(async (appointmentId: string) => {
    if (loading) return;
    setErrorMessage(null);

    setAllAppointmentsState((prev) => {
      const newMap = new Map(prev);
      newMap.forEach((appointments, guestId) => {
        if (appointments) {
          newMap.set(
            guestId,
            appointments.filter(
              (appointment) => appointment.appointmentId !== appointmentId
            )
          );
        }
      });
      return newMap;
    });
  }, [loading]);

  const contextValue = useMemo(() => ({
    loading, errorMessage, fetchAppointments, allAppointmentsState, deleteAppointment,
  }), [loading, errorMessage, fetchAppointments, allAppointmentsState, deleteAppointment]);

  return (
    <GuestAppointmentsContext.Provider
      value={contextValue}
    >
      {children}
    </GuestAppointmentsContext.Provider>
  );
};

export const useGuestAppointments = () => {
  const context = useContext(GuestAppointmentsContext);
  if (!context) {
    throw new Error(
      "useGuestAppointments must be used within a GuestAppointmentsProvider"
    );
  }
  return context;
};
